/**
 * Make a wrapped function that is throttled (it won't be called more than once
 * every `delayMs` milliseconds).
 *
 * @template {any[]} A
 * @param {(...args: A) => void} functionRef - The function to debounce
 * @param {number} [delayMs=300] - The delay in milliseconds
 * @param {boolean} [immediate=false] - Whether to execute the function immediately or after the delay
 * @returns {(...args: A) => void} The debounced function
 */
export function throttle(functionRef, delayMs = 300, immediate = false) {
  /** @type {number|undefined} */
  let timeoutId;

  return (...args) => {
    const callFunction = () => {
      timeoutId = undefined;
      if (!immediate) {
        functionRef(...args);
      }
    };

    const shouldCallImmediate = immediate && !timeoutId;

    clearTimeout(timeoutId);

    if (shouldCallImmediate) {
      functionRef(...args);
    }

    timeoutId = window.setTimeout(callFunction, delayMs);
  };
}

/**
 * Return a wrapped function that only calls the original function if the
 * arguments have changed.
 *
 * @template {any[]} A
 * @param {(...args: A) => void} functionRef - The function to debounce
 * @returns {(...args: A) => void} The debounced function
 */
export function callIfArgsChanged(functionRef) {
  let lastArgs = null;

  const areEqual = function (lastArgs, newArgs) {
    return JSON.stringify(lastArgs) === JSON.stringify(newArgs);
  };

  return function (...args) {
    if (!lastArgs || !areEqual(lastArgs, args)) {
      lastArgs = args;
      return functionRef(...args);
    }
  };
}
